import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce, isEqual } from 'lodash';
import { useAppSelector } from '../../../hooks';
import { Button } from 'src/components-dummy';
import { CampaignSideBar } from './components/CampaignSideBar/CampaignSideBar';
import { RuleFormFooter } from 'src/components-bl/MerchandisingRules/components/MerchandisingRuleForm/RuleFormFooter';
import { HTMLSnippet } from './components/HTMLSnippet';
import { CampaignPreview } from './components/CampaignPreview/CampaignPreview';
import { fetchTemplate } from './fetch-template';
import { defaultWidgetMakerConfig } from '../constants/default-widget-maker-config';
import { campaignModalPageActions } from './Actions';
import { needsSku } from './needs-sku';
import { RoutedComponentProps } from 'src/app-routes';
import { Dispatch } from 'src/components-dummy/types';
import { Campaign } from '../types/campaign';
import { CampaignDraft } from '../types/campaign-draft';
import { CampaignStrategy } from '../types/campaign-strategy';
import {
  CreateCampaignPageStyled,
  SideBarLayout,
  PreviewLayout,
  OverlayModalStyled,
} from './CampaignModalPage.styles';

interface CampaignModalPageProps extends RoutedComponentProps {
  shopId: number;
  dispatch: Dispatch;
  navigateToCampaigns?: () => void;
  currentCampaign?: Campaign;
  hasDraft: boolean;
  isCreateMode?: boolean;
}

export const CampaignModalPage = ({
  shopId,
  dispatch,
  navigateToCampaigns,
  currentCampaign,
  hasDraft,
  isCreateMode,
}: CampaignModalPageProps): JSX.Element => {
  const { selectedOffer, template, templateLoading, templateError } = useAppSelector(
    state => state.campaigns
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isCreateValid, setIsCreateValid] = useState(false);
  const [showSnippet, setShowSnippet] = useState(false);
  const isDuplicateMode = isCreateMode && currentCampaign !== undefined;

  const getInitialDraft = (): CampaignDraft => {
    if (currentCampaign) {
      return { ...currentCampaign };
    }

    return {
      campaignName: '',
      strategy: undefined,
      collectionId: undefined,
      widgetMaker: { ...defaultWidgetMakerConfig },
    };
  };

  const initialCampaignDraft = useMemo(() => getInitialDraft(), [currentCampaign, isCreateMode]);
  const [campaignDraft, setCampaignDraft] = useState<CampaignDraft>(initialCampaignDraft);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [templateData, setTemplateData] = useState<string | undefined>(template);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);

  useEffect(() => {
    setCampaignDraft(initialCampaignDraft);
  }, [initialCampaignDraft]);

  const { campaignName, strategy, collectionId, widgetMaker } = campaignDraft;

  const shouldFetchTemplate = needsSku.has(strategy)
    ? !!selectedOffer?.sku
    : strategy === CampaignStrategy.Collections
      ? !!collectionId
      : !!strategy;

  useEffect(() => {
    if (!shouldFetchTemplate) {
      return;
    }
    const debouncedTemplate = debounce(async () => {
      await fetchTemplate({
        isPreview: true,
        setIsPreview,
        dispatch,
        shopId,
        strategy,
        collectionId,
        widgetMaker,
        isMobile,
        setTemplateData,
        sku: selectedOffer?.sku,
      });
    }, 300);

    debouncedTemplate();

    return () => {
      debouncedTemplate.cancel();
    };
  }, [
    campaignDraft,
    dispatch,
    shopId,
    isMobile,
    selectedOffer,
    isPreview,
    strategy,
    collectionId,
    widgetMaker,
    setTemplateData,
  ]);

  useEffect(() => {
    setIsDirty(!isEqual(initialCampaignDraft, campaignDraft) || isDuplicateMode);
  }, [campaignDraft, initialCampaignDraft, isDuplicateMode]);

  const handleFormValidationChange = (isValid: boolean) => {
    setIsCreateValid(isValid);
  };

  const onSave = useCallback(async () => {
    try {
      setIsLoading(true);

      const campaignPayload = {
        shopId,
        campaignName,
        strategy,
        collectionId,
        widgetMaker: {
          ...defaultWidgetMakerConfig,
          ...widgetMaker,
        },
      };

      const action = isCreateMode
        ? campaignModalPageActions.createCampaign(campaignPayload)
        : campaignModalPageActions.updateCampaign({
            shopId,
            campaignId: currentCampaign.id,
            payload: campaignPayload,
          });

      await (dispatch(action) as any).unwrap();

      await fetchTemplate({
        isPreview: false,
        setIsPreview,
        dispatch,
        shopId,
        strategy,
        collectionId,
        widgetMaker,
        isMobile,
        setTemplateData,
      });

      dispatch(campaignModalPageActions.setSelectedOffer(null));

      setShowSnippet(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    shopId,
    campaignDraft,
    dispatch,
    isCreateMode,
    currentCampaign,
    setIsPreview,
    strategy,
    collectionId,
    widgetMaker,
    isMobile,
    setTemplateData,
  ]);

  const onCancel = () => {
    setCampaignDraft(initialCampaignDraft);
    navigateToCampaigns();
  };

  const handleCloseSnippet = () => {
    setShowSnippet(false);
    setIsDirty(false);
    if (strategy === CampaignStrategy.Collections) {
      setIsPreview(prev => !prev);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(campaignModalPageActions.resetCurrentTemplate());
      dispatch(campaignModalPageActions.setSelectedOffer(null));
    };
  }, [dispatch]);

  const canSave = !isCreateValid || !isDirty;

  return shopId ? (
    <>
      <OverlayModalStyled show fullscreen={!hasDraft}>
        <CreateCampaignPageStyled>
          <SideBarLayout>
            <CampaignSideBar
              shopId={shopId}
              dispatch={dispatch}
              navigateToCampaigns={navigateToCampaigns}
              onFormValidationChange={handleFormValidationChange}
              campaignDraft={campaignDraft}
              setCampaignDraft={setCampaignDraft}
              isLoading={isLoading}
              isMobile={isMobile}
            />
          </SideBarLayout>
          <PreviewLayout>
            <CampaignPreview
              template={templateData}
              templateLoading={templateLoading}
              templateError={templateError}
              isMobile={isMobile}
              setIsMobile={setIsMobile}
              shopId={shopId}
              selectedOffer={selectedOffer}
              isPreview={isPreview}
            />
            <RuleFormFooter className='email-marketing-campaign'>
              <Button variant='tertiary' onClick={onCancel}>
                Cancel
              </Button>
              <Button variant='primary' disabled={canSave} onClick={onSave}>
                Save & Generate Snippet
              </Button>
            </RuleFormFooter>
          </PreviewLayout>
        </CreateCampaignPageStyled>
      </OverlayModalStyled>
      {showSnippet && (
        <HTMLSnippet
          dispatch={dispatch}
          onCloseModal={handleCloseSnippet}
          navigateToCampaigns={navigateToCampaigns}
          htmlSnippet={templateData}
        />
      )}
    </>
  ) : null;
};
